/* Shopify owns eligibility and all charged prices. URL amounts are never trusted. */
(() => {
  const normalize = (code) => typeof code === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(code.trim()) ? code.trim().toUpperCase() : '';
  const parseCode = (search) => {
    const params = new URLSearchParams(search);
    return normalize(params.get('ref') || params.get('ref_code') || params.get('referral_code') || params.get('code'));
  };
  const referralCookie = (cookie) => {
    const value = cookie.split(';').map(part => part.trim()).find(part => part.startsWith('discount_code='))?.slice(14);
    if (!value) return '';
    try {
      const code = normalize(decodeURIComponent(value));
      // Shopify's preview redirect can strip query parameters. Only recognize our referral namespace.
      return /^(SPEAKON-[A-Z0-9]{10}|REFERRAL-QA-15)$/.test(code) ? code : '';
    } catch (_) { return ''; }
  };
  const cents = (money) => money?.currencyCode === 'USD' ? Math.round(Number(money.amount) * 100) : NaN;
  const quotePrice = (cart, variantId, code) => {
    if (!code || !cart?.discountCodes?.some(d => normalize(d.code) === code && d.applicable)) return null;
    if (cart.discountCodes.some(d => d.applicable && normalize(d.code) !== code) ||
        cart.lines?.nodes?.some(l => l.discountAllocations?.some(d => normalize(d.code) !== code))) return null;
    const line = cart.lines?.nodes?.find(l => l.merchandise.id === `gid://shopify/ProductVariant/${variantId}`);
    if (!line || !Number.isInteger(line.quantity) || line.quantity < 1) return null;
    const discounts = line.discountAllocations || [];
    const original = cents(line.cost.amountPerQuantity);
    const total = cents(line.cost.totalAmount);
    const allocation = discounts.filter(d => normalize(d.code) === code).reduce((sum, d) => sum + cents(d.discountedAmount), 0);
    // Only advertise the agreed USD offer when Shopify confirms exactly $15 per unit.
    if (original !== 12900 || total !== 11400 * line.quantity || allocation !== 1500 * line.quantity || discounts.length !== 1 || cart.discountAllocations?.length) return null;
    return { original, final: total / line.quantity };
  };
  const appliedToCart = (cart, code) => cart?.currency === 'USD' && !!code && !cart.cart_level_discount_applications?.length && cart.items?.some(item =>
    item.handle === 'speakon-device' && item.original_price === 12900 && item.final_price === 11400 &&
    item.line_level_discount_allocations?.length === 1 && item.line_level_discount_allocations.some(d =>
      normalize(d.discount_application?.title) === code && d.amount === 1500 * item.quantity));
  if (typeof document === 'undefined') { module.exports = { normalize, parseCode, referralCookie, quotePrice, appliedToCart }; return; }
  const configNode = document.getElementById('friend-referral-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const route = window.Shopify?.routes?.root || '/';
  const storageKey = 'speakon.friend-referral';
  const entryCode = parseCode(location.search);
  const cookieCode = referralCookie(document.cookie);
  let saved = '';
  try { saved = sessionStorage.getItem(storageKey); } catch (_) { /* Storage is optional. */ }
  let code = entryCode || normalize(saved) || cookieCode;
  if (!code) return;
  const saveCode = () => { try { code ? sessionStorage.setItem(storageKey, code) : sessionStorage.removeItem(storageKey); } catch (_) {} };
  saveCode();
  const productRoot = config.product === 'speakon-device' ? document.querySelector('.as-variants-data')?.closest('.shopify-section') : null;
  let price = null;
  let cartActive = false;
  let status = '';
  let revision = 0;
  let applying = 0;
  let pending = Promise.resolve();
  let checking = false;
  const money = (value, compact = false) => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: 'USD', minimumFractionDigits: compact ? 0 : 2 }).format(value / 100);
  const request = async (path, body) => {
    const response = await fetch(path, { method: body ? 'POST' : 'GET', headers: { 'Content-Type': 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}), signal: AbortSignal.timeout(12000) });
    if (!response.ok) throw new Error(`Shopify returned ${response.status}`);
    return response.json();
  };
  const syncAttribution = async (cart) => {
    const verifiedCode = cartActive || price ? code : '';
    if ((cart.attributes?.ref_code || '') !== verifiedCode) {
      await request(`${route}cart/update.js`, { attributes: { ref_code: verifiedCode } });
    }
  };
  function render() {
    productRoot?.querySelectorAll('.product-price[id^="price-"], .total-price-wrapper').forEach(parent => {
      let slot = parent.querySelector(':scope > .friend-referral-price');
      if (!slot) {
        slot = document.createElement('div');
        slot.className = 'friend-referral-price';
        slot.setAttribute('aria-live', 'polite');
        slot.innerHTML = '<div class="friend-referral-price-row"><strong></strong><s></s><span class="friend-referral-label"></span></div><p class="friend-referral-gift"><span aria-hidden="true">🎁</span> A friend sent you $15 off Coupon</p>';
        parent.append(slot);
      }
      const active = !!price;
      if (parent.classList.contains('friend-referral-active') !== active) parent.classList.toggle('friend-referral-active', active);
      slot.hidden = !active;
      if (active) {
        const quantity = parent.id.startsWith('price-total-') ? Number(productRoot.querySelector('[name="quantity"]')?.value || 1) : 1;
        for (const [tag, amount] of [['s', price.original], ['strong', price.final]]) {
          const node = slot.querySelector(tag), text = money(amount * quantity, tag === 'strong');
          if (node.textContent !== text) node.textContent = text;
        }
        const label = slot.querySelector('.friend-referral-label'), savings = `Save ${money((price.original - price.final) * quantity, true)}`;
        if (label.textContent !== savings) label.textContent = savings;
      }
    });
    const mainPrice = productRoot?.querySelector('.product-price[id^="price-"]');
    if (mainPrice) {
      let note = productRoot.querySelector('[data-referral-status]');
      if (!note) { note = document.createElement('p'); note.dataset.referralStatus = ''; note.className = 'friend-referral-note'; note.setAttribute('role', 'status'); mainPrice.after(note); }
      if (note.textContent !== status) note.textContent = status;
      note.hidden = !status;
    }
    document.querySelectorAll('[data-referral-discount]').forEach(node => {
      const active = cartActive && normalize(node.dataset.referralDiscount) === code;
      const text = active ? 'Friend Referral' : node.dataset.referralDiscount;
      if (node.textContent !== text) node.textContent = text;
    });
    document.querySelectorAll('[data-referral-combination-note]').forEach(node => { node.hidden = !cartActive; });
    productRoot?.querySelectorAll('.as-checkout-btn').forEach(link => {
      const url = new URL(link.href);
      if (price && code) { url.searchParams.set('discount', code); link.dataset.referralLinked = 'true'; }
      else if (link.dataset.referralLinked) { url.searchParams.delete('discount'); delete link.dataset.referralLinked; }
      if (url.href !== link.href) link.href = url.href;
    });
  }
  async function refresh(apply = false) {
    if (apply) applying++;
    const current = ++revision;
    price = null;
    cartActive = false;
    status = code ? 'Checking your referral code…' : '';
    render();
    try {
      let cart = await request(`${route}cart.js`);
      if (current !== revision) return;
      if (apply && code) {
        cart = await request(`${route}cart/update.js`, { discount: code });
        // Reuse the theme's normal cart rendering and discount manager.
        await document.querySelector('discount-manager')?.init();
        document.dispatchEvent(new CustomEvent('cart.update_discount', { bubbles: true, detail: { referralRefresh: true } }));
      }
      if (current !== revision) return;
      cartActive = appliedToCart(cart, code);
      if (!productRoot || !code) { status = ''; await syncAttribution(cart); render(); return; }
      const variantId = productRoot.querySelector('.as-product-variant')?.value;
      const quantity = Number(productRoot.querySelector('input[name="quantity"]')?.value || 1);
      if (!variantId || !Number.isInteger(quantity) || quantity < 1) throw new Error('Invalid product selection');
      const selectedCodes = (cart.discount_codes || []).map(d => d.code);
      const discounts = selectedCodes.length ? selectedCodes : [code];
      const lines = cart.items.map(item => ({ merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`, quantity: item.quantity,
        ...(item.selling_plan_allocation ? { sellingPlanId: `gid://shopify/SellingPlan/${item.selling_plan_allocation.selling_plan.id}` } : {}) }));
      const selected = lines.find(line => line.merchandiseId === `gid://shopify/ProductVariant/${variantId}` && !line.sellingPlanId);
      if (selected) selected.quantity += quantity;
      else lines.push({ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity });
      // ponytail: quote a prospective cart; customer-specific eligibility is finalized by native checkout.
      const result = await request('/api/2026-07/graphql.json', {
        query: 'mutation ReferralQuote($input: CartInput!) { cartCreate(input: $input) { cart { discountCodes { code applicable } discountAllocations { discountedAmount { amount currencyCode } } lines(first: 250) { nodes { quantity merchandise { ... on ProductVariant { id } } cost { amountPerQuantity { amount currencyCode } totalAmount { amount currencyCode } } discountAllocations { ... on CartCodeDiscountAllocation { code } discountedAmount { amount currencyCode } } } } } userErrors { message } } }',
        variables: { input: { lines, discountCodes: discounts, buyerIdentity: { countryCode: config.country } } }
      });
      if (current !== revision) return;
      if (result.errors?.length || result.data?.cartCreate?.userErrors?.length || !result.data?.cartCreate?.cart) throw new Error('Quote unavailable');
      price = quotePrice(result.data.cartCreate.cart, variantId, code);
      status = price ? '' : 'This referral offer is not available for your selection. Shopify confirms discounts at checkout.';
      await syncAttribution(cart);
    } catch (_) {
      if (current !== revision) return;
      price = null;
      status = 'Unable to verify the referral offer. Shopify will confirm discounts at checkout.';
    } finally {
      if (apply) applying--;
    }
    if (current === revision) render();
  }
  const run = (apply = false) => {
    checking = true;
    const work = refresh(apply);
    pending = work;
    work.finally(() => { if (pending === work) checking = false; });
    return work;
  };
  // Existing theme rerenders price fragments and cart sections after variant/cart changes.
  const observer = new MutationObserver(render);
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
  let timer;
  productRoot?.addEventListener('change', event => {
    if (!event.target.matches('[name="id"], [name="quantity"], .as-product-variant')) return;
    ++revision; price = null; render(); clearTimeout(timer); timer = setTimeout(() => run(), 80);
  });
  document.addEventListener('cart.add', () => run(!!code));
  document.addEventListener('cart.update', () => run());
  document.addEventListener('cart.update_discount', event => {
    if (event.detail?.referralRefresh || applying) return;
    const codes = event.detail?.currentDiscountCodes;
    const applicable = event.detail?.applicableDiscountCodes;
    if ((Array.isArray(codes) && !codes.some(value => normalize(value) === code)) ||
        (applicable?.length && !applicable.some(value => normalize(value) === code))) {
      if (referralCookie(document.cookie) === code) document.cookie = 'discount_code=; Max-Age=0; Path=/';
      code = ''; saveCode();
    }
    run();
  });
  // Hold both Add to Cart forms until the initial discount application finishes.
  let submitting = false;
  document.addEventListener('submit', async event => {
    if (submitting || !checking || !productRoot?.contains(event.target) || !event.target.matches('[data-type="add-to-cart-form"]')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const form = event.target, submitter = event.submitter;
    await pending;
    // Leave the original submit event before asking the browser to submit again.
    setTimeout(() => {
      submitting = true;
      try { form.requestSubmit(submitter || undefined); } finally { submitting = false; }
    }, 0);
  }, true);
  // All main/sticky Buy Now links are rebuilt by the native theme after selection changes.
  document.addEventListener('click', async event => {
    const link = event.target.closest('.as-checkout-btn');
    if (!link || !productRoot?.contains(link) || !code) return;
    if (price && (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    await pending;
    const url = new URL(link.href);
    if (price && code) url.searchParams.set('discount', code);
    location.assign(url.href);
  }, true);
  run(!!entryCode || (!saved && !!cookieCode));
})();
